import {
  ActionIcon,
  Button,
  Group,
  Modal,
  ScrollArea,
  Table,
  Text,
  Tooltip,
  createStyles,
} from '@mantine/core';
import classNames from 'classnames';
import React, {useCallback, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/analytics-products/ModelHubAnalyticsConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {isS3URLExpired} from 'external-files/SignedExtFileURLProxy';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  PreviewSequenceNameCell,
  resolvePreviewSequenceItems,
  type PreviewSequenceDisplayItem,
} from './predictionInputSequences';

const WINDOW_OPEN_FEATURES = 'noopener,noreferrer';

function StructurePreviewViewer({downloadUrl}: {downloadUrl: string}): JSX.Element {
  const {classes} = useStyles();

  return (
    <div className={classes.modalViewerWrapper}>
      <MolstarViewer
        url={downloadUrl}
        format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER]}
        className={classNames('reset', classes.viewer)}
        interactionAnalyticsCallback={() => {
          trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.INTERACTED_MODEL_HUB_PREVIEW_VIEWER);
        }}
      />
    </div>
  );
}

function StructurePreviewActions({
  onExpand,
  onDownload,
  downloadRequesting,
}: {
  onExpand: () => void;
  onDownload: () => void;
  downloadRequesting: boolean;
}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Group
      spacing="sm"
      className={classes.previewActions}
      {...getTestProps({component: 'ResultsPreviewCell', element: 'structurePreviewActions'})}
    >
      <Tooltip label="Open in preview" withinPortal={true}>
        <ActionIcon aria-label="Open in preview" variant="tertiary" onClick={onExpand}>
          <BenchlingIcon icon="expand-right-full" />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Download structure" withinPortal={true}>
        <ActionIcon
          aria-label="Download structure"
          variant="tertiary"
          onClick={onDownload}
          disabled={downloadRequesting}
        >
          <BenchlingIcon icon="download" />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

function DownloadStandardizedOutputButton({
  downloadUrl,
  onRefreshDownloadUrls,
}: {
  downloadUrl: string;
  onRefreshDownloadUrls?: () => void;
}): JSX.Element {
  const onClick = (): void => {
    if (isS3URLExpired(downloadUrl)) {
      onRefreshDownloadUrls?.();
      return;
    }
    window.open(downloadUrl, '_blank', WINDOW_OPEN_FEATURES);
  };

  return (
    <Button variant="primary" onClick={onClick}>
      Download structure
    </Button>
  );
}

function StructurePreviewModal({
  opened,
  onClose,
  downloadUrl,
  onRefreshDownloadUrls,
  sequenceItems,
  sequencesLoading,
}: {
  opened: boolean;
  onClose: () => void;
  downloadUrl: string;
  onRefreshDownloadUrls?: () => void;
  sequenceItems: Array<PreviewSequenceDisplayItem>;
  sequencesLoading: boolean;
}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Preview"
      size="xl"
      centered={true}
      withinPortal={true}
      classNames={{
        inner: classes.modalInner,
        content: classes.modalContentWrapper,
        body: classes.modalBody,
      }}
    >
      <div className={classes.modalLayout}>
        <div className={classes.modalViewerSection}>
          <StructurePreviewViewer downloadUrl={downloadUrl} />
        </div>
        <div className={classes.modalSequencesSection}>
          {sequencesLoading ? (
            <CenteredHorizontalThrobber />
          ) : sequenceItems.length === 0 ? (
            <Text size="sm" color="dimmed" ta="center">
              No components configured for this job.
            </Text>
          ) : (
            <div className={classes.sequencesTableWrapper}>
              <ScrollArea className={classes.sequencesScrollArea} type="auto">
                <Table className={classes.sequencesTable} verticalSpacing="sm">
                  <thead>
                    <tr>
                      <th className={classes.headerCell}>Component</th>
                      <th className={classes.headerCell}>Sequence / SMILES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sequenceItems.map((item) => (
                      <tr key={item.key}>
                        <td className={classes.nameColumn}>
                          <PreviewSequenceNameCell item={item} />
                        </td>
                        <td className={classes.sequenceColumn}>{item.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </div>
        <div className={classes.modalFooter}>
          <DownloadStandardizedOutputButton
            downloadUrl={downloadUrl}
            onRefreshDownloadUrls={onRefreshDownloadUrls}
          />
        </div>
      </div>
    </Modal>
  );
}

export interface ResultsPreviewCellProps {
  standardizedOutputDownloadUrl: string | null;
  componentApiIds: ReadonlyArray<string>;
  modelName: string;
  configJson?: Record<string, unknown> | null;
  onRefreshDownloadUrls?: () => void;
}

export default function ResultsPreviewCell({
  standardizedOutputDownloadUrl,
  componentApiIds,
  modelName,
  configJson,
  onRefreshDownloadUrls,
}: ResultsPreviewCellProps): JSX.Element {
  const {classes} = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [sequenceItems, setSequenceItems] = useState<Array<PreviewSequenceDisplayItem>>([]);
  const [sequencesLoading, setSequencesLoading] = useState(false);

  const fetchSequenceItems = useCallback((): void => {
    setSequencesLoading(true);
    resolvePreviewSequenceItems(componentApiIds, modelName, configJson)
      .then((items) => {
        setSequenceItems(items);
      })
      .catch(() => {
        setSequenceItems([]);
      })
      .finally(() => {
        setSequencesLoading(false);
      });
  }, [componentApiIds, modelName, configJson]);

  const openPreviewModal = useCallback((): void => {
    if (standardizedOutputDownloadUrl == null) {
      return;
    }
    setModalOpen(true);
    fetchSequenceItems();
  }, [standardizedOutputDownloadUrl, fetchSequenceItems]);

  const closePreviewModal = useCallback((): void => {
    setModalOpen(false);
  }, []);

  const handleDownload = useCallback((): void => {
    if (standardizedOutputDownloadUrl == null) {
      return;
    }
    if (isS3URLExpired(standardizedOutputDownloadUrl)) {
      onRefreshDownloadUrls?.();
      return;
    }
    window.open(standardizedOutputDownloadUrl, '_blank', WINDOW_OPEN_FEATURES);
  }, [onRefreshDownloadUrls, standardizedOutputDownloadUrl]);

  if (standardizedOutputDownloadUrl == null) {
    return <span className={classes.noPreview}>—</span>;
  }

  const modal = (
    <StructurePreviewModal
      opened={modalOpen}
      onClose={closePreviewModal}
      downloadUrl={standardizedOutputDownloadUrl}
      onRefreshDownloadUrls={onRefreshDownloadUrls}
      sequenceItems={sequenceItems}
      sequencesLoading={sequencesLoading}
    />
  );

  return (
    <>
      <StructurePreviewActions
        onExpand={openPreviewModal}
        onDownload={handleDownload}
        downloadRequesting={false}
      />
      {modal}
    </>
  );
}

const useStyles = createStyles((theme) => ({
  previewActions: {
    flexWrap: 'nowrap',
  },
  modalViewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    height: 'min(560px, 65vh)',
    minHeight: 320,
    maxWidth: '100%',
    overflow: 'hidden',
    borderRadius: theme.radius.md,
    border: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  viewer: {
    borderWidth: 0,
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
    height: '100%',
  },
  modalInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContentWrapper: {
    margin: 'auto',
    width: 'min(760px, 95vw)',
    maxWidth: '95vw',
  },
  modalBody: {
    paddingTop: theme.spacing.sm,
    paddingBottom: 0,
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    marginTop: theme.spacing.lg,
    marginLeft: -theme.spacing.l,
    marginRight: -theme.spacing.l,
    marginBottom: -theme.spacing.l,
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.lg,
    paddingLeft: theme.spacing.l,
    paddingRight: theme.spacing.l,
  },
  noPreview: {
    color: Colors.GRAY_DARKER,
    fontSize: theme.fontSizes.sm,
  },
  modalLayout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing.lg,
    width: '100%',
  },
  modalViewerSection: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'stretch',
    width: '100%',
  },
  modalSequencesSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  sequencesTableWrapper: {
    boxSizing: 'border-box',
    width: '100%',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
  },
  sequencesScrollArea: {
    width: '100%',
    maxHeight: 280,
  },
  sequencesTable: {
    width: '100%',
    tableLayout: 'fixed',
    '& tbody tr:not(:last-child) td': {
      borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    },
  },
  headerCell: {
    textAlign: 'left',
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  nameColumn: {
    width: '35%',
    verticalAlign: 'top',
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.4,
    borderRight: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  sequenceColumn: {
    verticalAlign: 'top',
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
}));
